import type { AddressSubValue } from '@checkout-page/schemas';
import { getAddressRule } from '../../lib/addressRules';
import REGION_ALIASES from './regionAliases.json';

declare global {
  interface Window {
    google?: any;
    __GOOGLE_MAPS_API_KEY__?: string;
  }
}

export type Suggestion = {
  placeId: string;
  primaryText: string;
  secondaryText: string;
};

type PlacesLib = {
  AutocompleteSuggestion: any;
  Place: any;
  AutocompleteSessionToken: new () => any;
};

let placesLib: PlacesLib | null = null;
let loadPromise: Promise<PlacesLib> | null = null;

export function loadPlacesLibrary(): Promise<PlacesLib> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (placesLib) return Promise.resolve(placesLib);
  if (loadPromise) return loadPromise;

  const key =
    (process.env as any).NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
    (process.env as any).REACT_APP_GOOGLE_MAPS_API_KEY ||
    window.__GOOGLE_MAPS_API_KEY__;
  if (!key) return Promise.reject(new Error('no key'));

  loadPromise = new Promise<PlacesLib>((resolve, reject) => {
    const importPlaces = async () => {
      try {
        const lib = await window.google.maps.importLibrary('places');
        placesLib = {
          AutocompleteSuggestion: lib.AutocompleteSuggestion,
          Place: lib.Place,
          AutocompleteSessionToken: lib.AutocompleteSessionToken,
        };
        resolve(placesLib);
      } catch (e) {
        reject(e);
      }
    };

    // Wait for the bootstrap to define google.maps.importLibrary, then call it.
    // Direct-loading the API script (no inline bootstrap) means `load` fires
    // before importLibrary is attached to the namespace, so we poll.
    const waitForImportLibrary = () => {
      if (window.google?.maps?.importLibrary) {
        void importPlaces();
        return;
      }
      const pollId = window.setInterval(() => {
        if (window.google?.maps?.importLibrary) {
          window.clearInterval(pollId);
          void importPlaces();
        }
      }, 50);
      window.setTimeout(() => {
        window.clearInterval(pollId);
        reject(new Error('importLibrary unavailable after timeout'));
      }, 10_000);
    };

    const existing = document.querySelector<HTMLScriptElement>('script[data-google-places]');
    if (existing) {
      waitForImportLibrary();
      return;
    }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly`;
    s.async = true;
    s.dataset.googlePlaces = 'true';
    s.addEventListener('load', waitForImportLibrary);
    s.addEventListener('error', () => reject(new Error('load failed')));
    document.head.appendChild(s);
  }).catch(err => {
    loadPromise = null;
    throw err;
  });

  return loadPromise;
}

export function createSessionToken(): any {
  if (!placesLib) throw new Error('placesLib not loaded — call loadPlacesLibrary first');
  return new placesLib.AutocompleteSessionToken();
}

type AddressComponent = { types?: string[]; shortText: string | null; longText: string | null };

const NUMBER_AFTER_STREET = new Set([
  'AM',
  'AR',
  'AT',
  'BA',
  'BE',
  'BG',
  'BO',
  'BR',
  'CH',
  'CL',
  'CO',
  'CR',
  'CU',
  'CZ',
  'DE',
  'DK',
  'DO',
  'EC',
  'EE',
  'ES',
  'FI',
  'GR',
  'GT',
  'HN',
  'HR',
  'HU',
  'ID',
  'IS',
  'IT',
  'LT',
  'LV',
  'MX',
  'NI',
  'NL',
  'NO',
  'PA',
  'PE',
  'PL',
  'PT',
  'PY',
  'RO',
  'RS',
  'RU',
  'SE',
  'SI',
  'SK',
  'SR',
  'SV',
  'TR',
  'UA',
  'UY',
  'VE',
]);
const NUMBER_AFTER_COMMA = new Set(['BR', 'ES', 'IT', 'PT', 'RU', 'UA']);

const CITY_FALLBACKS: Record<string, string[]> = {
  US: ['sublocality_level_1'],
  BR: ['administrative_area_level_2'],
  EG: ['administrative_area_level_2'],
  ID: ['administrative_area_level_2'],
  KR: ['sublocality_level_1'],
  TH: ['administrative_area_level_2', 'sublocality_level_1'],
  TR: ['administrative_area_level_2'],
  TW: ['administrative_area_level_2'],
  VN: ['administrative_area_level_2'],
};

// Beijing, Tianjin, Shanghai and Chongqing are both province and city; Google sends no locality.
const CN_MUNICIPALITIES = new Set(['11', '12', '31', '50']);

function cnMunicipalityName(region?: string): string | undefined {
  if (!region || !CN_MUNICIPALITIES.has(region)) return undefined;
  return getAddressRule('CN').regions?.find(r => r.code === region)?.name;
}

function formatLine1(route?: string, number?: string, country?: string): string {
  if (!route || !number) return route || number || '';
  if (!country || !NUMBER_AFTER_STREET.has(country)) return `${number} ${route}`;
  if (country === 'ID' && !/^no\b/i.test(number)) return `${route} No. ${number}`;
  return NUMBER_AFTER_COMMA.has(country) ? `${route}, ${number}` : `${route} ${number}`;
}

export function mapPlaceToAddress(place: any): Partial<AddressSubValue> {
  const comps: AddressComponent[] = place?.addressComponents ?? [];
  const get = (type: string) => comps.find(c => c.types?.includes(type))?.shortText ?? undefined;
  const getLong = (type: string) => comps.find(c => c.types?.includes(type))?.longText ?? undefined;

  const streetNumber = get('street_number');
  const route = getLong('route');
  const country = get('country')?.toUpperCase();
  const line1 = formatLine1(route, streetNumber, country);
  const region = resolveRegion(comps, country);
  const cityFallback = (country && CITY_FALLBACKS[country]) || [];

  return {
    line1: line1 || undefined,
    line2: getLong('subpremise') || undefined,
    city:
      getLong('locality') ||
      getLong('postal_town') ||
      cityFallback.map(getLong).find(Boolean) ||
      (country === 'CN' ? cnMunicipalityName(region) : undefined) ||
      undefined,
    region,
    postalCode: get('postal_code') || undefined,
    country: country || undefined,
  };
}

// Google returns region as ISO 3166-2 short codes for some countries (US "CA", CA "ON")
// but as localized full names for many others (ID "Bali", TH "Bangkok"). Our region
// <select> expects codes, so we resolve names back to codes — otherwise the browser
// silently picks the first non-disabled option (e.g. always "Aceh" for ID).
function resolveRegion(comps: AddressComponent[], country: string | undefined): string | undefined {
  const levels = (country && REGION_LEVELS[country]) || ['administrative_area_level_1'];
  const adminComps = levels
    .map(level => comps.find(c => c.types?.includes(level)))
    .filter((c): c is AddressComponent => !!c);
  if (adminComps.length === 0) return undefined;

  const regions = country ? getAddressRule(country).regions : undefined;
  if (!country || !regions || regions.length === 0) return adminComps[0].shortText ?? undefined;

  for (const c of adminComps) {
    const code = matchRegionCode(country, [c.shortText ?? undefined, c.longText ?? undefined]);
    if (code) return code;
  }
  return undefined;
}

// Our lists for these countries are provinces; Google's level 1 is the larger region above
// them. Ceuta, Melilla and Metro Manila have no level 2, so ES and PH fall back to level 1.
const REGION_LEVELS: Record<string, string[]> = {
  ES: ['administrative_area_level_2', 'administrative_area_level_1'],
  IT: ['administrative_area_level_2'],
  PH: ['administrative_area_level_2', 'administrative_area_level_1'],
};

const REGION_PREFIXES = [
  'chang wat ',
  'departamento de ',
  'departamento del ',
  'provincia de ',
  'provincia del ',
  'province of ',
  'estado de ',
  'state of ',
];
const REGION_SUFFIXES = [
  ' province',
  ' department',
  ' parish',
  ' governorate',
  ' oblast',
  ' region',
  ' state',
  ' prefecture',
];

export function normalizeRegionName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .normalize('NFC')
    .toLowerCase()
    .replace(/đ/g, 'd')
    .replace(/[.'’`,\-–]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripAffixes(name: string): string {
  let out = name;
  for (const p of REGION_PREFIXES) if (out.startsWith(p)) out = out.slice(p.length);
  for (const x of REGION_SUFFIXES) if (out.endsWith(x)) out = out.slice(0, -x.length);
  return out;
}

export function matchRegionCode(
  country: string,
  candidates: (string | undefined)[],
): string | undefined {
  const regions = getAddressRule(country).regions ?? [];
  const names = candidates.filter((c): c is string => !!c);

  for (const cand of names) {
    const byCode = regions.find(r => r.code === cand);
    if (byCode) return byCode.code;
  }

  for (const cand of names) {
    const n = normalizeRegionName(cand);
    const byName = regions.find(r => normalizeRegionName(r.name) === n);
    if (byName) return byName.code;
    const alias = (REGION_ALIASES as Record<string, Record<string, string>>)[country]?.[n];
    if (alias) return alias;
  }

  for (const cand of names) {
    const n = stripAffixes(normalizeRegionName(cand));
    const hits = regions.filter(r => stripAffixes(normalizeRegionName(r.name)) === n);
    if (hits.length === 1) return hits[0].code;
  }

  return undefined;
}

export async function fetchSuggestions(args: {
  input: string;
  country: string | undefined;
  sessionToken: any;
  signal?: AbortSignal;
}): Promise<Suggestion[]> {
  const { input, country, sessionToken, signal } = args;
  if (!input.trim()) return [];
  if (!placesLib) return [];

  const request: any = {
    input,
    sessionToken,
    includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
    language: typeof navigator !== 'undefined' ? navigator.language : undefined,
  };
  if (country) request.includedRegionCodes = [country];

  const { suggestions } =
    await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

  if (signal?.aborted) return [];

  return suggestions
    .map((s: any) => s.placePrediction)
    .filter(Boolean)
    .map((p: any) => ({
      placeId: p.placeId,
      primaryText: p.mainText?.text ?? p.text?.text ?? '',
      secondaryText: p.secondaryText?.text ?? '',
    }));
}

export async function fetchPlaceDetails(args: {
  placeId: string;
  sessionToken: any;
}): Promise<any> {
  const { placeId, sessionToken } = args;
  if (!placesLib) throw new Error('placesLib not loaded');
  const place = new placesLib.Place({ id: placeId });
  await place.fetchFields({
    fields: ['addressComponents', 'formattedAddress'],
    sessionToken,
  });
  return place;
}
